
import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Search, 
  Filter, 
  Clock, 
  Zap, 
  Star,
  Target, 
  Dumbbell,
  ArrowLeft,
  Loader2,
  Eye
} from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import ProfileIconLink from "@/components/ProfileIconLink";
import WorkoutLibraryFiltersDrawer from "@/components/client/WorkoutLibraryFiltersDrawer";
import { listTemplates, type WorkoutTemplateMeta } from "@/integrations/backend/workout-templates";
import { toast } from "sonner";
import { CATALOG_STALE_BASE_MS, staleTimeWithJitterFromKey } from "@/lib/query-cache";
import { queryKeys } from "@/lib/query-keys";
import {
  applyWorkoutLibraryFilters,
  countActiveWorkoutFilters,
  DEFAULT_WORKOUT_LIBRARY_FILTER_STATE,
  type WorkoutLibraryFilterState,
  type WorkoutLibraryFilters,
  type WorkoutTemplateDisplay,
} from "@/lib/workout-library-filters";

export type { WorkoutTemplateDisplay, WorkoutLibraryFilterState, WorkoutLibraryFilters };
export {
  applyWorkoutLibraryFilters,
  countActiveWorkoutFilters,
  DEFAULT_WORKOUT_LIBRARY_FILTER_STATE,
} from "@/lib/workout-library-filters";

type Props = { onBack?: () => void };

const TEMPLATE_DIFFICULTY_LEVELS = ["Beginner", "Intermediate", "Advanced", "Expert", "Pro"] as const;

function mapTemplateToDisplay(template: WorkoutTemplateMeta): WorkoutTemplateDisplay {
    // Determine difficulty levels + primary difficulty from difficulty_counts
    const difficultyLevels = TEMPLATE_DIFFICULTY_LEVELS.filter(
      (lvl) => (template.difficulty_counts?.[lvl] ?? 0) > 0
    );

    let primaryDifficulty: string = difficultyLevels[0] ?? "Beginner";
    if (template.difficulty_counts) {
      const counts = template.difficulty_counts;
      const maxCount = Math.max(
        counts.Beginner || 0,
        counts.Intermediate || 0,
        counts.Advanced || 0,
        counts.Expert || 0,
        counts.Pro || 0
      );
      if (maxCount > 0) {
        for (const lvl of [...TEMPLATE_DIFFICULTY_LEVELS].reverse()) {
          if ((counts[lvl] || 0) === maxCount) {
            primaryDifficulty = lvl;
            break;
          }
        }
      }
    }

    const category =
      (template.category && template.category.trim()) ||
      (template.primary_muscles_top && template.primary_muscles_top.length > 0
        ? "Strength"
        : "General Fitness");

    return {
      id: template.id,
      title: template.name,
      description: `Workout template with ${template.exercise_count || 0} exercises`,
      duration: template.duration_minutes || 30,
      difficulty: primaryDifficulty,
      difficultyLevels: difficultyLevels.length ? [...difficultyLevels] : [primaryDifficulty],
      category,
      goals: ["fitness"], // Default since not available in API
      equipment: [], // Not available in API
      exercises: template.exercise_count || 0,
      rating: 4.5, // Not displayed but kept for type compatibility
      muscleGroups: template.primary_muscles_top || [],
      estimatedCalories: 0 // Not displayed
    };
}

const WorkoutLibrary = ({ onBack }: Props) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterState, setFilterState] = useState<WorkoutLibraryFilterState>(
    DEFAULT_WORKOUT_LIBRARY_FILTER_STATE
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const templatesListKey = queryKeys.workoutTemplates.list();
  const templatesQuery = useQuery({
    queryKey: templatesListKey,
    queryFn: listTemplates,
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...templatesListKey]),
    select: (apiTemplates: WorkoutTemplateMeta[]) => apiTemplates.map(mapTemplateToDisplay),
  });
  const templates = templatesQuery.data ?? [];
  const isLoading = templatesQuery.isPending;
  const error = templatesQuery.error
    ? templatesQuery.error instanceof Error
      ? templatesQuery.error.message
      : String(templatesQuery.error)
    : null;

  const isMobile = useIsMobile();
  const navigate = useNavigate();

  useEffect(() => {
    if (!templatesQuery.isError || !templatesQuery.error) return;
    const errorMessage =
      templatesQuery.error instanceof Error
        ? templatesQuery.error.message
        : "Failed to load workout templates";
    toast.error("Could not load workout templates", { description: errorMessage });
  }, [templatesQuery.isError, templatesQuery.error]);

  const muscleOptions = useMemo(
    () =>
      [...new Set(templates.flatMap((t) => t.muscleGroups))].sort((a, b) =>
        a.localeCompare(b)
      ),
    [templates]
  );

  const categoryOptions = useMemo(() => {
    const fromTemplates = [
      ...new Set(
        templates
          .map((t) => t.category?.trim())
          .filter((c): c is string => Boolean(c) && c !== "General Fitness")
      ),
    ].sort((a, b) => a.localeCompare(b));
    return fromTemplates;
  }, [templates]);

  const activeFilterCount = countActiveWorkoutFilters(filterState);

  // Get user preferences from localStorage (from onboarding)
  const userPreferences = useMemo(() => {
    try {
      const stored = localStorage.getItem('onboardingFormData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const filterParams: WorkoutLibraryFilters = useMemo(
    () => ({ searchQuery, ...filterState }),
    [searchQuery, filterState]
  );

  // Filter workouts based on search and filters
  const filteredWorkouts = useMemo(() => {
    return applyWorkoutLibraryFilters(templates, filterParams);
  }, [templates, filterParams]);

  const isFiltering =
    searchQuery.trim().length > 0 || activeFilterCount > 0;

  // Get recommended workouts based on user preferences
  const recommendedWorkouts = useMemo(() => {
    const base = isFiltering ? filteredWorkouts : templates;
    if (base.length === 0) return [];
    if (!userPreferences) return base.slice(0, 3);

    return base
      .filter(workout => {
        const matchesGoals = userPreferences.goals?.some((goal: string) => 
          workout.goals.includes(goal.toLowerCase().replace(' ', '-'))
        );
        const matchesExerciseTypes = userPreferences.exerciseTypes?.some((type: string) => 
          workout.category.toLowerCase() === type.toLowerCase()
        );
        return matchesGoals || matchesExerciseTypes;
      })
      .slice(0, 3);
  }, [userPreferences, templates, filteredWorkouts, isFiltering]);

  const handleStartWorkout = (template: WorkoutTemplateDisplay) => {
    navigate(`/view-workout/${template.id}`);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "Beginner": return "bg-strength text-white";
      case "Intermediate": return "bg-mobility text-black";
      case "Advanced": return "bg-cardio text-white";
      case "Expert": return "bg-cardio text-white";
      case "Pro": return "bg-cardio text-white";
      default: return "bg-dark-slate text-white";
    }
  };

  return (
    <div className="min-h-screen gradient-dark">
      {/* Header */}
      <div className={`bg-gradient-to-r from-dark-bg to-dark-card text-white p-6 pt-8 pb-10 rounded-b-[2.5rem] border-b-2 border-primary/20 ${!isMobile ? "md:ml-16" : ""}`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            {onBack && (
              <Button 
                variant="ghost" 
                className="bg-white/10 rounded-full h-9 px-3 flex items-center justify-center hover:bg-white/20"
                onClick={onBack}
                aria-label="Back to Explore"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                <span className="text-sm">Back to Explore</span>
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              className="relative bg-white/10 rounded-full h-10 w-10 flex items-center justify-center p-0 hover:bg-white/20"
              onClick={() => setFiltersOpen(true)}
              aria-label="Open filters"
            >
              <Filter className="w-5 h-5" />
              {activeFilterCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-black">
                  {activeFilterCount}
                </span>
              )}
            </Button>
            <ProfileIconLink />
          </div>
        </div>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Workout Library</h1>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary w-5 h-5" />
          <Input
            placeholder="Search workouts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-dark-card border border-primary/10 text-white placeholder:text-gray-light"
          />
        </div>
      </div>

      <WorkoutLibraryFiltersDrawer
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        filters={filterState}
        onFiltersChange={setFilterState}
        muscleOptions={muscleOptions}
        categoryOptions={categoryOptions}
        resultCount={filteredWorkouts.length}
      />

      <div className={`p-6 space-y-6 ${isMobile ? "pb-24" : ""} ${!isMobile ? "md:ml-16" : ""}`}>
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <span className="ml-3 text-white">Loading workout templates...</span>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <Card className="border border-red-500/50 bg-red-500/10">
            <CardContent className="p-4 text-center text-white">
              <p className="text-red-300">Error loading templates: {error}</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Recommended for You */}
        {!isLoading && !error && recommendedWorkouts.length > 0 && (
          <div>
            <h2 className="text-xl font-bold mb-4 flex items-center text-white">
              <Star className="w-5 h-5 mr-2 text-primary" />
              Recommended for You
            </h2>
            <div className="flex flex-col gap-3">
              {recommendedWorkouts.map(workout => (
                <WorkoutCard 
                  key={workout.id} 
                  workout={workout} 
                  onStart={handleStartWorkout}
                  getDifficultyColor={getDifficultyColor}
                />
              ))}
            </div>
          </div>
        )}

        {/* All Workouts */}
        {!isLoading && !error && (
          <div>
            <h2 className="text-xl font-bold mb-4 text-white">
              All Workouts ({filteredWorkouts.length})
            </h2>
            {filteredWorkouts.length > 0 ? (
              <div className="flex flex-col gap-3">
                {filteredWorkouts.map(workout => (
                  <WorkoutCard 
                    key={workout.id} 
                    workout={workout} 
                    onStart={handleStartWorkout}
                    getDifficultyColor={getDifficultyColor}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Target className="w-16 h-16 text-gray-light mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No workouts found</h3>
                <p className="text-gray-light">Try adjusting your search or filters</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

type WorkoutCardProps = {
  workout: WorkoutTemplateDisplay;
  onStart: (workout: WorkoutTemplateDisplay) => void;
  getDifficultyColor: (difficulty: string) => string;
};

const WorkoutCard = ({ workout, onStart, getDifficultyColor }: WorkoutCardProps) => {
  const difficultyLevels =
    Array.isArray(workout.difficultyLevels) && workout.difficultyLevels.length > 0
      ? workout.difficultyLevels
      : [workout.difficulty];
  const visibleMuscles = workout.muscleGroups?.slice(0, 4) ?? [];
  const hiddenMuscleCount = Math.max((workout.muscleGroups?.length ?? 0) - visibleMuscles.length, 0);

  return (
    <Card className="border border-primary/20 bg-dark-card shadow-card hover:shadow-neon-glow transition-shadow">
      <CardContent className="p-3 text-white space-y-2.5">
        <div className="flex items-start gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <Dumbbell className="h-4 w-4 text-primary" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-base leading-tight line-clamp-2">{workout.title}</h3>
            <p className="text-gray-light text-xs mt-0.5 line-clamp-1">{workout.description}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {difficultyLevels.map((lvl) => (
            <Badge key={lvl} variant="secondary" className={`text-xs px-2 py-0 ${getDifficultyColor(lvl)}`}>
              {lvl}
            </Badge>
          ))}
          <Badge variant="outline" className="text-xs px-2 py-0 border-primary/30 text-white">
            {workout.category}
          </Badge>
        </div>

        {visibleMuscles.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {visibleMuscles.map((muscle) => (
              <Badge
                key={muscle}
                variant="outline"
                className="text-xs px-2 py-0 border-primary/20 text-gray-light bg-dark-slate/50"
              >
                {muscle}
              </Badge>
            ))}
            {hiddenMuscleCount > 0 && (
              <Badge
                variant="outline"
                className="text-xs px-2 py-0 border-primary/20 text-gray-light bg-dark-slate/50"
              >
                +{hiddenMuscleCount}
              </Badge>
            )}
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-gray-light">
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" aria-hidden="true" />
            {workout.duration} min
          </span>
          <span className="inline-flex items-center gap-1">
            <Zap className="h-3.5 w-3.5" aria-hidden="true" />
            {workout.exercises} exercises
          </span>
        </div>

        <Button
          size="sm"
          className="w-full gradient-primary text-black hover:opacity-90"
          onClick={() => onStart(workout)}
        >
          <Eye className="h-4 w-4 mr-1.5" aria-hidden="true" />
          View Workout
        </Button>
      </CardContent>
    </Card>
  );
};

export default WorkoutLibrary;
